import { Box, Flex } from "@chakra-ui/react"
import * as React from "react"
import { useRecoilValue, useRecoilState } from "recoil"
import { hasPermission } from "../../../util"
import { EmotionalIconsProps } from "./buttons-main-screen.interface"
import layoutStates from "../../../recoil/atoms/layout"
import TabSwitcher from "./tab-switcher"
import {
  currentVisibleOverlayAtom,
  showBadgeOverlayAtom
} from "../../../recoil/atoms/overlay"
import { chatCountAtom } from "../../../recoil/atoms/chats"
import { pollsAtom } from "../../../recoil/atoms/polls"
import { taggedBadgesListSelector } from "../../../recoil/atoms/taggedBadges"
import { shoppableMNPVideoAtom } from "../../../recoil/atoms/shoppableVideos"
import { badgeHistoryListSelector } from "../../../recoil/atoms/badgeHistory"
import { shoppableVideosListSelector } from "../../../recoil/atoms/shoppableVideos"
import { HeartIcon } from "./heart-icon"
import { HandIcon } from "./hand-icon"
import { DesktopMainScreenButtons } from "./desktop-main-screen-buttons"
import { ShareIcon } from "./share-icon"
import { brandingSelector, showTabsAtBottomOnMobileSelector } from "../../../recoil/atoms/room"
import { LeaveMeetingButton } from "../leave-overlay"
import useConfig from "src/hooks/useConfig"

const UserType = window.MeetOnline?.UserType
const UserPermission = window.MeetOnline?.UserPermission

const _EmotionalIcons: React.FC<EmotionalIconsProps> = ({
  isMobile,
  isTransparent,
  isLandscape,
  localUserProperties,
  isCustomPlayer,
  offTouch,
  isDesktop,
  changeSizeIconDependingHeightVideo,
  showLeaveButton = false
}) => {
  const showTabsAtBottomOnMobile = useRecoilValue(showTabsAtBottomOnMobileSelector)

  const roomStyles = useRecoilValue(brandingSelector)
  const {videoPlayerHandEnabled, videoPlayerHeartEnabled, videoPlayerShareEnabled} = useConfig()

  const isHandIcon = React.useMemo<boolean>(() => {
    return (
      localUserProperties?.type === UserType?.MODERATOR ||
      hasPermission(
        localUserProperties?.permissions,
        UserPermission.canRaiseHand
      )
    )
  }, [localUserProperties])

  const isHeartIcon = React.useMemo<boolean>(() => {
    return (
      localUserProperties?.type === UserType?.MODERATOR ||
      hasPermission(
        localUserProperties?.permissions,
        UserPermission.canUseEmoji
      )
    )
  }, [localUserProperties])

  const isHideFSScrollControlChat = useRecoilValue(
    layoutStates.hideFSScrollControlChat
  )
  const isHideFSScrollControlBadges = useRecoilValue(
    layoutStates.hideFSScrollControlBadges
  )

  const [targetTabIndex] = useRecoilState<number>(
    layoutStates.historyTabEnabledMobile
  )

  const isHideFSScrollControlVideos = useRecoilValue(
    layoutStates.hideFSScrollControlVideos
  )

  const _overlay_ = useRecoilValue(currentVisibleOverlayAtom)
  const [isOverlayBadge] = useRecoilState<boolean>(showBadgeOverlayAtom)

  const isOverlay = React.useMemo(
    () => _overlay_ && isOverlayBadge,
    [_overlay_, isOverlayBadge]
  )

  const isVisibleMobileFSTabs = useRecoilValue(layoutStates.visibleMobileFSTabs)
  const chatCount = useRecoilValue(chatCountAtom)
  const polls = useRecoilValue(pollsAtom)
  const allBadgeHistory = useRecoilValue(badgeHistoryListSelector)

  const mobilePoll = polls
    .filter(el => el.isVisible && el.isVisibleMobile)
    .sort((a, b) => (a?.timestamp < b?.timestamp ? -1 : 1))

  const { enabledShoppableMNP } = useRecoilValue(shoppableMNPVideoAtom)

  const taggedBadgesList = useRecoilValue(taggedBadgesListSelector)
  const shoppableVideosList = useRecoilValue(shoppableVideosListSelector)

  const mobileBadgesHistory = React.useMemo(() => {
    if (enabledShoppableMNP) {
      return taggedBadgesList
    }
    return allBadgeHistory
  }, [allBadgeHistory, enabledShoppableMNP, taggedBadgesList])

  const tab = useRecoilValue(layoutStates.activeTab)

  const enabledFSControls = React.useMemo(() => {
    return (
      (!isOverlay || showTabsAtBottomOnMobile) && // show button with badge overlay when bottom tab is used
      isTransparent &&
      (tab === "chat"
        ? !isHideFSScrollControlChat &&
          (mobilePoll.length !== 0 || chatCount !== 0)
        : tab === "watch"
        ? !isHideFSScrollControlVideos &&
          isVisibleMobileFSTabs &&
          shoppableVideosList.length !== 0
        : tab === "shop"
        ? !isHideFSScrollControlBadges && mobileBadgesHistory.length !== 0
        : false)
    )
  }, [
    chatCount,
    tab,
    isHideFSScrollControlBadges,
    isHideFSScrollControlChat,
    isHideFSScrollControlVideos,
    isOverlay,
    isTransparent,
    isVisibleMobileFSTabs,
    mobileBadgesHistory.length,
    mobilePoll.length,
    shoppableVideosList.length,
    targetTabIndex,
    showTabsAtBottomOnMobile
  ])

  return (
    <>
      {isDesktop ? (
        <DesktopMainScreenButtons
          localUserProperties={localUserProperties}
          isHandIcon={isHandIcon}
          isHeartIcon={isHeartIcon}
          showLeaveButton={showLeaveButton}
          showHandButton={!!videoPlayerHandEnabled}
          showHeartButton={!!videoPlayerHeartEnabled}
          showShareButton={!!videoPlayerShareEnabled}
        />
      ) : (
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          zIndex={2}
        >
          {
            // No overlay
            !isOverlay &&
              // and is Landscape or is transparent with no bottom tab
              ((isTransparent && !showTabsAtBottomOnMobile) || isLandscape) && (
                <TabSwitcher
                  isFSScreenButtonsControls={enabledFSControls}
                  isHeartIcon={isHeartIcon}
                />
              )
          }

          {videoPlayerShareEnabled && isHeartIcon && (
            <ShareIcon
              isMobile={true}
              changeSizeIconDependingHeightVideo={
                changeSizeIconDependingHeightVideo
              }
            />
          )}

          {videoPlayerHandEnabled && <HandIcon
            showIcon={isHandIcon}
            localUserProperties={localUserProperties}
            isHeartIcon={isHeartIcon}
            isMobile={true}
            changeSizeIconDependingHeightVideo={
              changeSizeIconDependingHeightVideo
            }
          />}

          {videoPlayerHeartEnabled && <HeartIcon
            showIcon={isHeartIcon}
            localUserProperties={localUserProperties}
            isMobile={!isDesktop}
            changeSizeIconDependingHeightVideo={
              changeSizeIconDependingHeightVideo
            }
          />}

          {showLeaveButton && (
            <Box mt="10px">
              <LeaveMeetingButton
                color={roomStyles?.overlayEmojiStrokeColor ?? "white"}
                backgroundColor={"rgba(0, 0, 0, 0.2)"}
              />
            </Box>
          )}

          {/* {isHideAudControls && (
            <div
              id="mute-icon"
              style={{
                marginBottom: isLandscape ? "17px" : "12px",
                marginTop: "3px"
              }}
              className="icon-ios"
            >
              <MuteIcon
                isMobile={isMobile}
                changeSizeIconDependingHeightVideo={
                  changeSizeIconDependingHeightVideo
                }
                isLandscape={isLandscape}
              />
            </div>
          )}

          {(!isWatchParty || isDisableVideoActive) &&
            localUserProperties?.localUserId !== mainWindowUser?.uid &&
            showMuteIncomingIcon && (
              <MuteUserButton
                uid={mainWindowUser.uid}
                isMainScreen={true}
                changeSizeIconDependingHeightVideo={
                  changeSizeIconDependingHeightVideo
                }
              />
            )} */}
        </Flex>
      )}
    </>
  )
}

export const EmotionalIcons = React.memo(_EmotionalIcons)
